/* About · /about — the verqo story, no names mentioned.
 * Authored Feb 2026 from the founder's brief: "verqo was built by
 * wholesalers and industry experts who lived the same struggles every
 * seller and buyer faces every day."
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Handshake, ShieldCheck, Zap, TrendingUp } from "lucide-react";
import { VerqoLockup } from "../components/VerqoLockup";
import MarketingMobileNav from "../components/MarketingMobileNav";

const PRINCIPLES = [
  {
    icon: Target,
    title: "Built by wholesalers, not bankers",
    body: "verqo was built by wholesalers and industry experts who lived the same struggles every seller and buyer faces every day — 60-day terms, frozen cash, seven vendors, paper invoices, ghost payments. We didn't read about it in a deck. We carried it on our P&L.",
  },
  {
    icon: Handshake,
    title: "Win-win is the only model that lasts",
    body: "Sellers want cash today. Buyers want time to pay. Lenders want underwritten risk. Insurers want clean books. Banks want compliance they can hand to an examiner. verqo is the layer where all five interests meet — not by compromise, but by design.",
  },
  {
    icon: ShieldCheck,
    title: "Partners chosen for substance, not optics",
    body: "We don't claim to be a bank — we partner with one. We don't underwrite ourselves — we share the risk with insurers and lenders. The names we trust to sit behind every transaction are the best in their categories. No middlemen, no hidden margin.",
  },
  {
    icon: Zap,
    title: "We charge for what we operate — nothing else",
    body: "We don't upcharge for things we don't run. We don't take a fee on every invoice you process. Our pricing is one transparent flat percentage on what we actually move — and the rest of the platform is yours to use. Most of what makes other tools expensive (extra modules, integrations, advanced reports) is included.",
  },
  {
    icon: TrendingUp,
    title: "Your growth, not your tooling",
    body: "The number one goal of verqo is to eliminate the manual work, the cash-flow stress, and the chase-the-payment phone calls that consume founders' time. We want you to focus on selling, sourcing, growing — not on reconciling spreadsheets or hand-wiring credit checks. We built this so you can stop worrying about plumbing and start scaling.",
  },
];

const STATS = [
  { value: "$0", label: "Per-invoice fee" },
  { value: "1", label: "Source of truth" },
  { value: "5", label: "Roles · one ledger" },
  { value: "60", label: "Seconds to credit decision" },
];

export default function About() {
  useEffect(() => { document.title = "About · verqo"; window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-[#f6f7f5] text-slate-900">
      {/* Header */}
      <header className="px-6 md:px-12 max-w-[1280px] mx-auto pt-8 pb-4 flex items-center justify-between" data-testid="about-header">
        <Link to="/" className="inline-flex items-center"><VerqoLockup size="sm" /></Link>
        <nav className="hidden lg:flex items-center gap-6 text-[12px] text-slate-600">
          <Link to="/" className="hover:text-slate-900">For sellers</Link>
          <Link to="/buyers" className="hover:text-slate-900">For buyers</Link>
          <Link to="/partners" className="hover:text-slate-900">Partners</Link>
          <Link to="/contact" className="hover:text-slate-900">Contact</Link>
        </nav>
        <MarketingMobileNav />
      </header>

      {/* Hero */}
      <section className="px-6 md:px-12 max-w-[920px] mx-auto pt-16 md:pt-24 pb-12" data-testid="about-hero">
        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.25em] text-lime-700 mb-5">
          <span className="w-2 h-2 rounded-full bg-lime-500"/>About verqo
        </div>
        <h1 className="text-[42px] md:text-[64px] leading-[0.98] tracking-tight font-semibold text-slate-900">
          Built by the people <br />
          <span className="text-slate-500">who lived the problem.</span>
        </h1>
        <p className="mt-7 max-w-xl text-[16px] md:text-[17px] leading-relaxed text-slate-600">
          verqo wasn't designed in a fintech accelerator. It was built by operators
          who spent years on the other side of every late invoice, every paperwork-
          heavy credit check, every "I'll get back to you" from a customer who knew
          they had 60 more days before they had to pay.
        </p>
      </section>

      {/* Stats strip */}
      <section className="px-6 md:px-12 max-w-[1280px] mx-auto pb-16 md:pb-24" data-testid="about-stats">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 border-y border-slate-200 py-8 md:py-12">
          {STATS.map((s, i) => (
            <div key={i} data-testid={`about-stat-${i}`}>
              <div className="text-[32px] md:text-[44px] font-semibold tracking-tight text-slate-900 tabular-nums">{s.value}</div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* The five principles */}
      <section className="px-6 md:px-12 max-w-[1080px] mx-auto pb-16 md:pb-24" data-testid="about-principles">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.25em] text-lime-700 mb-3">
            <span className="w-2 h-2 rounded-full bg-lime-500"/>What we believe
          </div>
          <h2 className="text-[28px] md:text-[40px] leading-tight tracking-tight font-semibold text-slate-900">
            Five things we won't compromise on.
          </h2>
        </div>
        <div className="space-y-10 md:space-y-14">
          {PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="flex gap-5 md:gap-8" data-testid={`about-principle-${i}`}>
                <div className="shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl bg-slate-900 grid place-items-center">
                  <Icon className="w-5 h-5 md:w-6 md:h-6 text-lime-300"/>
                </div>
                <div className="flex-1 pt-0.5">
                  <h3 className="text-[17px] md:text-[20px] font-semibold tracking-tight text-slate-900">{p.title}</h3>
                  <p className="mt-2.5 text-[14.5px] md:text-[15.5px] leading-relaxed text-slate-600">{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 max-w-[920px] mx-auto pb-24 md:pb-32" data-testid="about-cta">
        <div className="rounded-3xl bg-slate-900 text-white p-10 md:p-16">
          <h2 className="text-[26px] md:text-[36px] leading-tight tracking-tight font-semibold">
            Wholesale that finally feels <span className="text-lime-300">instant</span>.
          </h2>
          <p className="mt-4 max-w-lg text-[14.5px] md:text-[16px] text-white/70 leading-relaxed">
            We built verqo so you can run the business — not the spreadsheet.
            One ledger. Five roles. Real money rails. Bank-grade compliance baked in.
            And we don't charge for the things we don't move.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register?as=seller"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-lime-300 text-slate-900 text-[13.5px] font-semibold hover:bg-lime-200 transition-colors"
                  data-testid="about-cta-seller">
              I'm a seller <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to="/register?as=buyer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white/10 text-white text-[13.5px] font-semibold hover:bg-white/15 transition-colors border border-white/15"
                  data-testid="about-cta-buyer">
              I'm a buyer <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 md:px-12 max-w-[1280px] mx-auto py-10 border-t border-slate-200 flex flex-wrap gap-4 items-center justify-between text-[11px] text-slate-500" data-testid="about-footer">
        <div className="flex items-center gap-3"><VerqoLockup size="sm" /><span>· Wholesale, instant.</span></div>
        <div className="flex items-center gap-5">
          <Link to="/" className="hover:text-slate-900">Sellers</Link>
          <Link to="/buyers" className="hover:text-slate-900">Buyers</Link>
          <Link to="/about" className="hover:text-slate-900">About</Link>
          <Link to="/faq" className="hover:text-slate-900">FAQ</Link>
          <Link to="/partners" className="hover:text-slate-900">Partners</Link>
          <Link to="/contact" className="hover:text-slate-900">Contact</Link>
        </div>
        <div>© 2026 verqo</div>
      </footer>
    </div>
  );
}
